import { yupResolver } from '@hookform/resolvers/yup'
import { findIndex, map } from 'lodash-es'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import SelectField from '@/components/select-field'
import { Icon } from '@/common/components/Icon'
import { HtmlEditor } from '@/core/components/HtmlEditor'
import { cn } from '@/lib/utils'
import {
  CheckInField,
  CheckInFieldType,
  Languages,
} from '../../../common/models'
import FormGroup from '../../../core/components/FormGroup'
import {
  deleteCheckInField,
  getAllCheckInFields,
  getCheckInField,
  updateCheckInField,
} from '../../../core/services/check-in-fields'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import Loader from '@/common/components/Loader'

// Human-readable label for every backend CheckInFieldType enum value.
const TYPE_LABELS: Record<CheckInFieldType, string> = {
  EMAIL: 'Email',
  CITY: 'City',
  COUNTRY: 'Country',
  NATIONALITY: 'Nationality',
  BIRTHDATE: 'Birthdate',
  SEX: 'Sex',
  FIRST_NAME: 'First Name',
  LAST_NAME: 'Last Name',
  SELFIE: 'Selfie',
  ADDRESS: 'Address',
  PHONE: 'Phone',
  GUARDIAN_FIRST_NAME: 'Guardian First Name',
  GUARDIAN_LAST_NAME: 'Guardian Last Name',
  GUARDIAN_PHONE: 'Guardian Phone',
  EXTRA_COUNTRY: 'Extra Country',
  EXTRA_CITY: 'Extra City',
  EXTRA_DATE: 'Extra Date',
  EXTRA_EMAIL: 'Extra Email',
  EXTRA_PHONE: 'Extra Phone',
  EXTRA_PHOTO: 'Extra Photo',
  EXTRA_TEXT: 'Extra Text',
}

// Dropdown mirrors the backend enum exactly; labels are just for display.
export const FIELD_TYPE_OPTIONS: { value: CheckInFieldType; label: string }[] =
  (Object.keys(TYPE_LABELS) as CheckInFieldType[]).map((value) => ({
    value,
    label: TYPE_LABELS[value],
  }))

export interface CreateCheckInFieldModalProps {
  showModal: boolean
  field?: CheckInField
  setShowModal: (show: boolean) => void
  setFields: (data: CheckInField[]) => void
  editMode: boolean
  allLanguages: Languages[]
}

interface IFieldContent {
  locale: string
  name: string
}

interface IFieldValues {
  type: string
  name: string
  content?: IFieldContent[]
  mandatory: boolean
  published: boolean
}

export const CreateCheckInFieldModal = (
  props: CreateCheckInFieldModalProps,
): JSX.Element => {
  const { showModal, setShowModal, field, editMode, setFields, allLanguages } =
    props

  const [deleteModal, setDeleteModal] = useState(false)
  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)

  const schema = yup.object().shape({
    type: yup.string().required('Field is required'),
    name: yup.string().required('Field is required'),
    content: yup
      .array()
      .of(
        yup.object().shape({
          locale: yup.string().nullable().required('Field is required'),
          name: yup.string().required('Field is required'),
        }),
      )
      .required(),
    mandatory: yup.boolean().oneOf([true, false]).required().default(false),
    published: yup.boolean().oneOf([true, false]).required().default(false),
  })

  const {
    handleSubmit,
    control,
    register,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<IFieldValues>({
    mode: 'onChange',
    resolver: yupResolver<IFieldValues>(
      schema as unknown as yup.ObjectSchema<IFieldValues>,
    ),
    defaultValues: {
      type: '',
      name: '',
      mandatory: false,
      published: false,
      content: [],
    },
  })

  const contentWatch = watch('content')

  useEffect(() => {
    const temp = map(allLanguages, (lang) => ({
      locale: lang.locale,
      name: '',
    }))
    setValue('content', temp)

    if (field?.id) {
      ;(async () => {
        try {
          setLoading(true)
          const { data: fetched } = await getCheckInField(field.id!)
          if (fetched.translations && fetched.translations.length > 0) {
            map(fetched.translations, (trans) => {
              const index = findIndex(
                allLanguages,
                (l) => l.locale === trans.locale,
              )
              if (index !== -1) {
                setValue(`content.${index}.name`, trans.label)
                setValue(`content.${index}.locale`, trans.locale)
              }
            })
          }
          setValue('type', fetched.type, { shouldValidate: true })
          setValue('name', fetched.name, { shouldValidate: true })
          setValue('mandatory', fetched.mandatory, { shouldValidate: true })
          setValue('published', fetched.published, { shouldValidate: true })
        } catch (e) {
          if (e instanceof Error) {
            setIsError(true)
            setShowMessageBox(true)
            setMessage(e.message)
            setErrorStatus(
              (e as { response?: { status?: number } })?.response?.status,
            )
          } else {
            console.error(e)
          }
        } finally {
          setLoading(false)
        }
      })()
    }

    return () => {
      reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field, showModal])

  const handleClose = () => {
    setShowModal(false)
  }

  const submit = async (values: IFieldValues) => {
    const data: CheckInField = {
      type: values.type as CheckInFieldType,
      name: values.name,
      translations: (values.content ?? []).map((c) => ({
        locale: c.locale,
        label: c.name,
      })),
      mandatory: values.mandatory,
      published: values.published,
    }

    try {
      setLoading(true)
      const { status } = await updateCheckInField(
        field?.id ? { ...data, id: field.id } : data,
      )
      const { data: refreshed } = await getAllCheckInFields()
      setFields(refreshed)
      if (status === 200) {
        handleClose()
        setIsError(false)
        setShowMessageBox(true)
        setMessage(field?.id ? 'Update Successful' : 'Create Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = () => {
    setDeleteModal(true)
  }

  const deleteFieldFromModal = () => {
    // prettier-ignore
    (async () => {
      try {
        if (field) {
          const { status } = await deleteCheckInField([field.id!])
          const { data: refreshed } = await getAllCheckInFields()
          setFields(refreshed)
          if (status === 204) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Successful')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
    handleClose()
  }

  return (
    <>
      {loading && <Loader />}
      <Modal
        open={showModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              title="Check-In Field"
              subtitle={field ? field.name : 'Add New Check-In Field'}
              icon="text"
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2 border-t border-monochrome-120 pt-4">
            {editMode ? (
              <Button
                onClick={handleDelete}
                variant="destructive"
                className="mr-auto"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div className="min-w-10" />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                form="check-in-field-form"
                type="submit"
                variant="primary"
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                {field?.id ? 'Save' : 'Create'}
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="check-in-field-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup label="Field Type">
            <Controller
              name="type"
              control={control}
              render={({ field: typeField }) => (
                <SelectField
                  outline
                  placeholder="Select"
                  options={FIELD_TYPE_OPTIONS}
                  value={typeField.value}
                  onValueChange={typeField.onChange}
                  error={errors?.type?.message}
                  triggerClassName="h-[44px] rounded-[16px]"
                />
              )}
            />
          </FormGroup>

          <FormGroup label="Field Name">
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="Enter Name"
            />
          </FormGroup>

          {map(allLanguages, (language, index) => (
            <div
              className={cn(
                'flex w-full flex-col gap-3 rounded-[30px] border border-monochrome-120 bg-monochrome-120 p-5',
              )}
              key={language.id}
            >
              <FormGroup label="Language" wrapperClassName="mb-0">
                <Input
                  value={language.name}
                  readOnly
                  className="bg-monochrome-120"
                />
              </FormGroup>
              <HtmlEditor
                index={index}
                label="Language"
                name={`content.${index}.name`}
                variables={[]}
                control={control}
                value={contentWatch?.[index]?.name || ''}
                onChange={(data) => {
                  setValue(`content.${index}.name`, data)
                }}
                isError={!!errors?.content?.[index]?.name?.message}
              />
            </div>
          ))}

          <div className="space-y-4">
            <FormGroup
              label="Mandatory Field"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{ htmlFor: 'mandatory' }}
            >
              <Controller
                name="mandatory"
                control={control}
                render={({ field: cbField }) => (
                  <Checkbox
                    id="mandatory"
                    checked={Boolean(cbField.value)}
                    onCheckedChange={(checked) =>
                      cbField.onChange(Boolean(checked))
                    }
                  />
                )}
              />
            </FormGroup>

            <FormGroup
              label="Published"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{ htmlFor: 'published' }}
            >
              <Controller
                name="published"
                control={control}
                render={({ field: cbField }) => (
                  <Checkbox
                    id="published"
                    checked={Boolean(cbField.value)}
                    onCheckedChange={(checked) =>
                      cbField.onChange(Boolean(checked))
                    }
                  />
                )}
              />
            </FormGroup>
          </div>
        </form>
      </Modal>
      <DeleteConfirmationModal
        size="md"
        showDeleteConfirmationModal={deleteModal}
        setDeleteConfirmationModal={setDeleteModal}
        deleteTitle="Check-In Field"
        deleteFunc={deleteFieldFromModal}
        deletedItem={field?.name}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
